import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    getPurchasesByClientId,
    deletePendingPurchase,
    listQuotationsByClient,
    getClientById,
    getClientSummary,
    getClientPayments,
    getDeliveryGuides,
    duplicateQuotation,
} from '../api';
import { FiTrash2, FiCopy, FiEye, FiTag } from 'react-icons/fi';
import PurchaseFilters from '../components/PurchaseFilters';
import CancelPendingSaleModal from '../components/CancelPendingSaleModal';
import ClientPaymentModal from '../components/ClientPaymentModal';
import ClientCreditModal from '../components/ClientCreditModal';
import ClientQuotationCategoriesEditor from '../components/ClientQuotationCategoriesEditor';
import useCurrentUser from '../hooks/useCurrentUser';
import usePurchaseFilters from '../hooks/usePurchaseFilters';
import { hasPermission } from '../utils/permissions';
import { formatRut } from '../utils/rut';
import { getQuotationStatusLabel } from '../utils/quotationStatus';
import { getDeliveryGuideStatusLabel } from '../utils/deliveryGuideStatus';
import {
    getPaymentTerm,
    getCreditDueDate,
    isPurchaseOverdue,
} from '../utils/purchaseUtils';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from '../components/InlineLoading';
import { notify } from '../utils/alertBus';
import { CANCEL_REASON_CATEGORY_MAP } from '../constants/cancelReasonCategories';

const ClientPurchasesPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [purchases, setPurchases] = useState([]);
    const [quotations, setQuotations] = useState([]);
    const [deliveryGuides, setDeliveryGuides] = useState([]);
    const [clientSummary, setClientSummary] = useState(null);
    const [clientEmail, setClientEmail] = useState('');
    const [loadingClient, setLoadingClient] = useState(true);
    const [loadingSummary, setLoadingSummary] = useState(true);
    const [loadingPurchases, setLoadingPurchases] = useState(false);
    const [loadingQuotations, setLoadingQuotations] = useState(false);
    const [loadingGuides, setLoadingGuides] = useState(false);
    const [loadingClientPayments, setLoadingClientPayments] = useState(false);
    const [clientError, setClientError] = useState('');
    const [summaryError, setSummaryError] = useState('');
    const [purchasesError, setPurchasesError] = useState('');
    const [quotationsError, setQuotationsError] = useState('');
    const [guidesError, setGuidesError] = useState('');
    const [clientPaymentsError, setClientPaymentsError] = useState('');
    const [activeTab, setActiveTab] = useState('summary');
    const [documentTypeFilter, setDocumentTypeFilter] = useState('');
    const [paymentFilter, setPaymentFilter] = useState('');
    const [cancelTarget, setCancelTarget] = useState(null);
    const [clientPayments, setClientPayments] = useState([]);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showCreditModal, setShowCreditModal] = useState(false);
    const [expandedPurchases, setExpandedPurchases] = useState(() => new Set());
    const [expandedQuotations, setExpandedQuotations] = useState(() => new Set());
    const branchId = localStorage.getItem('selectedBranchId');
    const { user } = useCurrentUser();
    const { filters, setters } = usePurchaseFilters({
        defaultStartDate: '',
        defaultEndDate: '',
    });
    const { startDate, endDate, paymentTermFilter, overdueFilter } = filters;
    const canReadSales = hasPermission(user, 'sales.read');
    const canUpdateSale = hasPermission(user, 'sales.update');
    const canDeleteSale = hasPermission(user, 'sales.delete');
    const canReadQuotation = hasPermission(user, 'quotation.read');
    const canCreateQuotation = hasPermission(user, 'quotation.create');
    const canUpdateQuotation = hasPermission(user, 'quotation.update');
    const canUpdateClient = hasPermission(user, 'client.update');
    const canManageCredit = hasPermission(user, 'branch.access.any');
    const canManageClientPricing = hasPermission(user, 'client_pricing.manage');
    const canReadDeliveryGuides = hasPermission(user, 'delivery_guide.read');
    const hasBranchContext = Boolean(branchId);
    const docLabel = (purchase) => {
        const type = (purchase?.documentType || '').toLowerCase();
        if (type === 'boleta') return 'Boleta';
        if (type === 'factura') return 'Factura';
        return 'Factura';
    };
    const isSaleOpen = useCallback((purchase) => {
        const statusNormalized = (purchase?.saleStatus || '').toLowerCase();
        return statusNormalized === 'open' || statusNormalized === 'suspended';
    }, []);

    const formatMoney = (value) => Number(value || 0).toLocaleString('de-DE');

    const formatDateTime = (value) => {
        if (!value) return '-';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '-';
        return date.toLocaleString('es-CL');
    };

    const formatDate = (value) => {
        if (!value) return '-';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('es-CL');
    };
    const getCancelReasonText = useCallback((purchase) => {
        if (!purchase) return null;
        const category = String(purchase.cancelReasonCategory || '').trim().toLowerCase();
        const detail = String(purchase.cancelReasonDetail || '').trim();
        if (category) {
            const categoryLabel = CANCEL_REASON_CATEGORY_MAP[category]?.label || category;
            return detail ? `${categoryLabel}: ${detail}` : categoryLabel;
        }
        const legacyReason = String(purchase.cancelReason || '').trim();
        return legacyReason || null;
    }, []);
    const resolveLatestDate = useCallback((items, accessor) => {
        let latest = null;
        items.forEach((item) => {
            const value = accessor(item);
            if (!value) return;
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return;
            if (!latest || date > latest) {
                latest = date;
            }
        });
        return latest;
    }, []);
    const togglePurchaseDetails = (purchaseId) => {
        setExpandedPurchases((prev) => {
            const next = new Set(prev);
            if (next.has(purchaseId)) {
                next.delete(purchaseId);
            } else {
                next.add(purchaseId);
            }
            return next;
        });
    };
    const toggleQuotationDetails = (quotationId) => {
        setExpandedQuotations((prev) => {
            const next = new Set(prev);
            if (next.has(quotationId)) {
                next.delete(quotationId);
            } else {
                next.add(quotationId);
            }
            return next;
        });
    };
    const copyToClipboard = async (text) => {
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'absolute';
                textarea.style.left = '-9999px';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            notify('Copiado al portapapeles.');
        } catch (error) {
            console.error('Error copying card', error);
            notify('No se pudo copiar.', { type: 'error' });
        }
    };
    const buildPurchaseCopy = (purchase) => {
        const status = purchase.paymentStatus || (purchase.isPaid ? 'paid' : 'unpaid');
        const statusLabel = {
            unpaid: 'Pendiente',
            partial: 'Parcial',
            paid: 'Pagada',
        };
        const statusNormalized = (purchase.saleStatus || '').toLowerCase();
        const isCancelled = statusNormalized === 'cancelled' || purchase.isDeleted;
        const term = getPaymentTerm(purchase);
        const dueDate = getCreditDueDate(purchase);
        const overdue = isPurchaseOverdue(purchase);
        const saleStatusLabel = isCancelled
            ? 'Eliminada'
            : statusNormalized === 'suspended'
                ? 'Suspendida'
                : statusLabel[status] || 'Pendiente';
        const cancelReasonText = getCancelReasonText(purchase);
        const clientLabel = purchase?.Client?.name || clientName;
        const rutLabel = clientRut && clientRut !== '—' ? `RUT: ${clientRut}` : null;

        return [
            `${docLabel(purchase)} #${purchase.id}`,
            `Cliente: ${clientLabel}`,
            rutLabel,
            `Total: $${formatMoney(purchase.totalAmount)}`,
            `Estado: ${saleStatusLabel}`,
            `Condicion: ${term === 'credit' ? 'Credito' : 'Contado'}`,
            purchase.balance !== undefined
                ? `Pendiente: $${formatMoney(purchase.balance || 0)}`
                : null,
            term === 'credit' && dueDate
                ? `Vence: ${formatDate(dueDate)}${overdue ? ' (Vencido)' : ''}`
                : null,
            isCancelled ? `Motivo anulacion: ${cancelReasonText || 'Sin motivo registrado'}` : null,
            `Fecha: ${formatDateTime(purchase.createdAt)}`,
        ]
            .filter(Boolean)
            .join('\n');
    };
    const buildQuotationCopy = (quotation) => {
        return [
            `Cotizacion #${quotation.id}`,
            `Estado: ${getQuotationStatusLabel(quotation.status)}`,
            `Total: $${formatMoney(quotation.totalAmount || 0)}`,
            `Fecha: ${formatDateTime(quotation.createdAt)}`,
            quotation.updatedAt ? `Actualizada: ${formatDateTime(quotation.updatedAt)}` : null,
            `Email cliente: ${clientEmail || 'Sin email'}`,
        ]
            .filter(Boolean)
            .join('\n');
    };
    const fetchClient = useCallback(async () => {
        setLoadingClient(true);
        setClientError('');
        try {
            const c = await getClientById(id);
            setClient(c);
            setClientEmail(c.email || '');
        } catch (err) {
            console.error(err);
            setClientError('No se pudo cargar la informacion del cliente.');
        } finally {
            setLoadingClient(false);
        }
    }, [id]);

    const fetchSummary = useCallback(async () => {
        setLoadingSummary(true);
        setSummaryError('');
        try {
            const summary = await getClientSummary(id);
            setClientSummary(summary);
        } catch (err) {
            console.error(err);
            setSummaryError('No se pudo cargar el resumen del cliente.');
        } finally {
            setLoadingSummary(false);
        }
    }, [id]);

    const fetchPurchases = useCallback(async () => {
        if (!hasBranchContext) {
            setPurchases([]);
            setLoadingPurchases(false);
            return;
        }
        if (!canReadSales) {
            setPurchases([]);
            setPurchasesError('No tienes permisos para ver compras.');
            setLoadingPurchases(false);
            return;
        }
        setLoadingPurchases(true);
        setPurchasesError('');
        try {
            const data = await getPurchasesByClientId(branchId, id, { pageSize: 0 });
            const normalized = normalizePaginatedResponse(data);
            setPurchases(normalized.data || []);
        } catch (err) {
            console.error(err);
            setPurchasesError('Error al cargar las compras del cliente.');
        } finally {
            setLoadingPurchases(false);
        }
    }, [branchId, canReadSales, hasBranchContext, id]);

    const fetchQuotations = useCallback(async () => {
        if (!hasBranchContext) {
            setQuotations([]);
            setLoadingQuotations(false);
            return;
        }
        if (!canReadQuotation) {
            setQuotations([]);
            setLoadingQuotations(false);
            return;
        }
        setLoadingQuotations(true);
        setQuotationsError('');
        try {
            const data = await listQuotationsByClient(branchId, id, { pageSize: 0 });
            const normalized = normalizePaginatedResponse(data);
            setQuotations(normalized.data || []);
        } catch (err) {
            console.error(err);
            setQuotationsError('Error al cargar las cotizaciones.');
        } finally {
            setLoadingQuotations(false);
        }
    }, [branchId, canReadQuotation, hasBranchContext, id]);

    const fetchGuides = useCallback(async () => {
        if (!hasBranchContext) {
            setDeliveryGuides([]);
            setLoadingGuides(false);
            return;
        }
        if (!canReadDeliveryGuides) {
            setDeliveryGuides([]);
            setLoadingGuides(false);
            return;
        }
        setLoadingGuides(true);
        setGuidesError('');
        try {
            const data = await getDeliveryGuides(branchId, { clientId: id, pageSize: 0 });
            const normalized = normalizePaginatedResponse(data);
            setDeliveryGuides(normalized.data || []);
        } catch (err) {
            console.error(err);
            setGuidesError('Error al cargar las guias de despacho.');
        } finally {
            setLoadingGuides(false);
        }
    }, [branchId, canReadDeliveryGuides, hasBranchContext, id]);

    const fetchClientPayments = useCallback(async () => {
        setLoadingClientPayments(true);
        setClientPaymentsError('');
        try {
            const data = await getClientPayments(id, { pageSize: 0 });
            const normalized = normalizePaginatedResponse(data);
            setClientPayments(normalized.data || []);
        } catch (err) {
            console.error(err);
            setClientPaymentsError('Error al cargar los pagos del cliente.');
        } finally {
            setLoadingClientPayments(false);
        }
    }, [id]);

    useEffect(() => {
        fetchClient();
        fetchSummary();
    }, [fetchClient, fetchSummary]);

    useEffect(() => {
        fetchPurchases();
        fetchQuotations();
        fetchGuides();
        fetchClientPayments();
    }, [fetchPurchases, fetchQuotations, fetchGuides, fetchClientPayments]);

    const handleDelete = (purchase) => {
        if (!canDeleteSale) return;
        if (!purchase?.id) return;
        setCancelTarget(purchase);
    };

    const handleConfirmCancel = async (cancelPayload) => {
        if (!canDeleteSale) return;
        if (!branchId || !cancelTarget?.id) {
            throw new Error('No se pudo identificar la venta.');
        }
        try {
            await deletePendingPurchase(branchId, cancelTarget.id, cancelPayload || {});
            setPurchases((prev) =>
                prev.map((purchase) =>
                    purchase.id === cancelTarget.id ? { ...purchase, isDeleted: true } : purchase
                )
            );
        } catch (error) {
            console.error('Error deleting purchase:', error.message);
            throw new Error(
                error?.response?.data?.message || 'No se pudo anular la venta pendiente.'
            );
        }
    };

    const handlePaymentSaved = async () => {
        await Promise.all([fetchSummary(), fetchPurchases(), fetchClientPayments()]);
    };

    const handleCreditSaved = async (updates = {}) => {
        setClient((prev) => (prev ? { ...prev, ...updates } : prev));
        await fetchClient();
    };

    const applyHistoryFilters = useCallback(
        (purchase) => {
            if (!purchase) return false;
            const term = getPaymentTerm(purchase);
            if (paymentTermFilter && term !== paymentTermFilter) return false;
            const overdue = isPurchaseOverdue(purchase);
            if (overdueFilter === 'overdue' && !overdue) return false;
            if (overdueFilter === 'current' && overdue) return false;
            return true;
        },
        [paymentTermFilter, overdueFilter]
    );

    const filteredPurchases = purchases.filter((purchase) => {
        if (isSaleOpen(purchase)) return false;
        if (!applyHistoryFilters(purchase)) return false;
        const documentType = (purchase?.documentType || '').toLowerCase();
        if (documentTypeFilter && documentType !== documentTypeFilter) return false;

        const term = getPaymentTerm(purchase);
        const status = purchase.paymentStatus || (purchase.isPaid ? 'paid' : 'unpaid');
        if (paymentFilter === 'credit' && term !== 'credit') return false;
        if (paymentFilter === 'paid') {
            if (term === 'credit') return false;
            if (status !== 'paid') return false;
        }
        return true;
    });

    const filteredQuotations = quotations.filter((q) => {
        const date = new Date(q.createdAt).toISOString().split('T')[0];
        const withinDateRange = (!startDate || date >= startDate) && (!endDate || date <= endDate);
        return withinDateRange;
    });

    const filteredGuides = deliveryGuides.filter((guide) => {
        const guideDate = guide.deliveryDate || guide.createdAt;
        if (!guideDate) return true;
        const date = new Date(guideDate).toISOString().split('T')[0];
        return (!startDate || date >= startDate) && (!endDate || date <= endDate);
    });

    const purchasePaymentRows = useMemo(() => {
        const rows = [];
        purchases.forEach((purchase) => {
            const payments = purchase.PurchasePayments || [];
            payments.forEach((payment) => {
                rows.push({
                    payment,
                    purchase,
                    paidAt: payment.paidAt || payment.createdAt,
                });
            });
        });
        return rows.sort((a, b) => new Date(b.paidAt) - new Date(a.paidAt));
    }, [purchases]);

    const filteredPurchasePayments = useMemo(() => {
        if (!startDate && !endDate) return purchasePaymentRows;
        return purchasePaymentRows.filter(({ paidAt }) => {
            const date = paidAt ? new Date(paidAt).toISOString().split('T')[0] : '';
            if (!date) return false;
            return (!startDate || date >= startDate) && (!endDate || date <= endDate);
        });
    }, [purchasePaymentRows, startDate, endDate]);

    const clientPaymentRows = useMemo(() => {
        return (clientPayments || [])
            .map((payment) => ({
                payment,
                paidAt: payment.paidAt || payment.createdAt,
            }))
            .sort((a, b) => new Date(b.paidAt) - new Date(a.paidAt));
    }, [clientPayments]);

    const filteredClientPayments = useMemo(() => {
        if (!startDate && !endDate) return clientPaymentRows;
        return clientPaymentRows.filter(({ paidAt }) => {
            const date = paidAt ? new Date(paidAt).toISOString().split('T')[0] : '';
            if (!date) return false;
            return (!startDate || date >= startDate) && (!endDate || date <= endDate);
        });
    }, [clientPaymentRows, startDate, endDate]);

    const openPurchases = useMemo(
        () => purchases.filter((purchase) => !isSaleOpen(purchase) && Number(purchase.balance || 0) > 0),
        [purchases, isSaleOpen]
    );
    const closedPurchases = useMemo(
        () => purchases.filter((purchase) => !isSaleOpen(purchase)),
        [purchases, isSaleOpen]
    );
    const closedPurchasesTotal = useMemo(
        () => closedPurchases.reduce((acc, purchase) => acc + Number(purchase.totalAmount || 0), 0),
        [closedPurchases]
    );
    const closedPurchasesBalance = useMemo(
        () => closedPurchases.reduce((acc, purchase) => acc + Number(purchase.balance || 0), 0),
        [closedPurchases]
    );
    const closedPurchasesOverdue = useMemo(
        () =>
            closedPurchases.reduce(
                (acc, purchase) => (isPurchaseOverdue(purchase) ? acc + Number(purchase.balance || 0) : acc),
                0
            ),
        [closedPurchases]
    );
    const closedPurchasesLatest = useMemo(
        () => resolveLatestDate(closedPurchases, (purchase) => purchase.createdAt),
        [closedPurchases, resolveLatestDate]
    );
    const quotationsTotal = useMemo(
        () => quotations.reduce((acc, quotation) => acc + Number(quotation.totalAmount || 0), 0),
        [quotations]
    );
    const quotationsLatest = useMemo(
        () => resolveLatestDate(quotations, (quotation) => quotation.updatedAt || quotation.createdAt),
        [quotations, resolveLatestDate]
    );
    const guidesLatest = useMemo(
        () => resolveLatestDate(deliveryGuides, (guide) => guide.deliveryDate || guide.createdAt),
        [deliveryGuides, resolveLatestDate]
    );

    const clientName = client?.name || 'Cliente';
    const clientRut = client?.rut ? formatRut(client.rut) : '—';
    const clientPricingCategoryName = client?.clientPricingLabel || client?.PricingCategory?.name || '';
    const summaryBalance = Number(clientSummary?.balance || 0);
    const summaryCredit = Number(clientSummary?.credit || 0);
    const summaryOverdue = Number(clientSummary?.overdue || 0);
    const summaryTotal = Number(clientSummary?.totalPurchases || 0);
    const effectiveBalance = hasBranchContext ? closedPurchasesBalance : summaryBalance;
    const effectiveOverdue = hasBranchContext ? closedPurchasesOverdue : summaryOverdue;

    const tabConfig = [
        { id: 'summary', label: 'Resumen', requiresBranch: false },
        { id: 'purchases', label: 'Compras', requiresBranch: true, allowed: canReadSales },
        { id: 'quotations', label: 'Cotizaciones', requiresBranch: true, allowed: canReadQuotation },
        { id: 'payments', label: 'Cobranza', requiresBranch: false, allowed: canReadSales },
        { id: 'guides', label: 'Guias', requiresBranch: true, allowed: canReadDeliveryGuides },
    ].filter((tab) => tab.allowed !== false);

    const shouldShowFilters = ['purchases', 'quotations', 'payments', 'guides'].includes(activeTab);

    const renderSummary = () => {
        const creditLimitLabel =
            client?.creditLimit !== null && client?.creditLimit !== undefined
                ? `$${formatMoney(client.creditLimit)}`
                : 'Sin limite';
        const summaryCards = [
            {
                id: 'purchases',
                label: 'Compras',
                tab: 'purchases',
                requiresBranch: true,
                allowed: canReadSales,
                stats: [
                    {
                        label: hasBranchContext ? 'Total cerrado (sucursal)' : 'Total compras',
                        value: hasBranchContext
                            ? `$${formatMoney(closedPurchasesTotal)}`
                            : `$${formatMoney(summaryTotal)}`,
                    },
                    {
                        label: 'Compras cerradas',
                        value: hasBranchContext ? String(closedPurchases.length) : '—',
                    },
                    {
                        label: 'Ultima compra',
                        value:
                            hasBranchContext && closedPurchasesLatest
                                ? formatDateTime(closedPurchasesLatest)
                                : '—',
                    },
                ],
            },
            {
                id: 'quotations',
                label: 'Cotizaciones',
                tab: 'quotations',
                requiresBranch: true,
                allowed: canReadQuotation,
                stats: [
                    {
                        label: 'Total cotizaciones',
                        value: hasBranchContext ? String(quotations.length) : '—',
                    },
                    {
                        label: 'Monto total',
                        value: hasBranchContext ? `$${formatMoney(quotationsTotal)}` : '—',
                    },
                    {
                        label: 'Ultima cotizacion',
                        value:
                            hasBranchContext && quotationsLatest
                                ? formatDateTime(quotationsLatest)
                                : '—',
                    },
                ],
            },
            {
                id: 'payments',
                label: 'Cobranza',
                tab: 'payments',
                requiresBranch: false,
                allowed: canReadSales,
                stats: [
                    {
                        label: 'Saldo pendiente',
                        value: `$${formatMoney(effectiveBalance)}`,
                    },
                    {
                        label: 'Vencido',
                        value: `$${formatMoney(effectiveOverdue)}`,
                    },
                    { label: 'Credito a favor', value: `$${formatMoney(summaryCredit)}` },
                    { label: 'Limite de credito', value: creditLimitLabel },
                    { label: 'Credito bloqueado', value: client?.creditBlocked ? 'Si' : 'No' },
                ],
                footer: (
                    <div className="mt-3 space-y-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-xs text-gray-500">
                                Ultimo pago:{' '}
                                {loadingSummary ? 'Cargando...' : formatDateTime(clientSummary?.lastPaymentAt)}
                            </span>
                            {canUpdateClient && (
                                <button
                                    onClick={() => setShowCreditModal(true)}
                                    className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                                    type="button"
                                >
                                    Editar credito
                                </button>
                            )}
                        </div>
                        {client?.creditWarningNote && (
                            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                                Nota: {client.creditWarningNote}
                            </div>
                        )}
                    </div>
                ),
            },
            {
                id: 'guides',
                label: 'Guias',
                tab: 'guides',
                requiresBranch: true,
                allowed: canReadDeliveryGuides,
                stats: [
                    {
                        label: 'Guias registradas',
                        value: hasBranchContext ? String(deliveryGuides.length) : '—',
                    },
                    {
                        label: 'Ultima guia',
                        value:
                            hasBranchContext && guidesLatest
                                ? formatDate(guidesLatest)
                                : '—',
                    },
                ],
            },
        ];

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {summaryCards.map((card) => {
                    const isDisabled = !card.allowed || (card.requiresBranch && !hasBranchContext);
                    return (
                        <div
                            key={card.id}
                            className="border border-gray-200 rounded-lg p-4 bg-white flex flex-col justify-between"
                        >
                            <div>
                                <div className="flex items-start justify-between gap-2">
                                    <p className="text-xs uppercase text-gray-500">{card.label}</p>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (isDisabled) return;
                                            setActiveTab(card.tab);
                                        }}
                                        disabled={isDisabled}
                                        className={`text-xs px-2 py-1 rounded border ${
                                            isDisabled
                                                ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                                                : 'border-blue-200 text-blue-600 hover:bg-blue-50'
                                        }`}
                                    >
                                        Ver detalle
                                    </button>
                                </div>
                                <div className="mt-3 space-y-1 text-sm text-gray-700">
                                    {card.stats.map((stat) => (
                                        <div key={stat.label} className="flex items-center justify-between gap-2">
                                            <span className="text-xs text-gray-500">{stat.label}</span>
                                            <span className="text-sm font-semibold text-gray-800">
                                                {stat.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                {isDisabled && (
                                    <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded p-2">
                                        {!card.allowed
                                            ? 'No tienes permisos para ver el detalle.'
                                            : 'Selecciona una sucursal para ver el detalle.'}
                                    </p>
                                )}
                            </div>
                            {card.footer}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderPurchases = () => {
        if (!hasBranchContext) {
            return <p className="text-sm text-gray-600">Selecciona una sucursal para ver compras.</p>;
        }
        if (loadingPurchases) {
            return (
                <p className="text-sm text-gray-600">
                    <InlineLoading label="Cargando compras" />
                </p>
            );
        }
        if (purchasesError) {
            return <p className="text-sm text-red-600">{purchasesError}</p>;
        }
        if (filteredPurchases.length === 0) {
            return <p className="text-sm text-gray-600">Sin compras con los filtros seleccionados.</p>;
        }
        return (
            <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredPurchases.map((purchase) => {
                    const status = purchase.paymentStatus || (purchase.isPaid ? 'paid' : 'unpaid');
                    const statusLabel = {
                        unpaid: 'Pendiente',
                        partial: 'Parcial',
                        paid: 'Pagada',
                    };
                    const isSuspended = purchase.saleStatus === 'suspended';
                    const statusNormalized = (purchase.saleStatus || '').toLowerCase();
                    const isCancelled = statusNormalized === 'cancelled' || purchase.isDeleted;
                    const term = getPaymentTerm(purchase);
                    const isClosed = term === 'credit' || status === 'paid';
                    const dueDate = getCreditDueDate(purchase);
                    const overdue = isPurchaseOverdue(purchase);
                    const canDelete = status === 'unpaid' && canDeleteSale && !isCancelled;
                    const statusTone = isCancelled
                        ? 'bg-red-100 text-red-700'
                        : isSuspended
                            ? 'bg-orange-100 text-orange-700'
                            : isClosed
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700';
                    const statusDisplayLabel = isCancelled
                        ? 'Eliminada'
                        : isSuspended
                            ? 'Suspendida'
                            : statusLabel[status] || 'Pendiente';
                    const cancelReasonText = getCancelReasonText(purchase);
                    const isExpanded = expandedPurchases.has(purchase.id);
                    const detailsId = `client-purchase-${purchase.id}-details`;

                    return (
                        <li
                            key={purchase.id}
                            className="border border-gray-200 rounded-lg p-4 shadow-sm bg-white"
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <p className="text-xs text-gray-500">
                                        {docLabel(purchase)} #{purchase.id}
                                    </p>
                                    <h3 className="text-lg font-semibold">{purchase.Client?.name || clientName}</h3>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <span className={`text-xs px-2 py-1 rounded ${statusTone}`}>
                                        {statusDisplayLabel}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => togglePurchaseDetails(purchase.id)}
                                        className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded hover:bg-gray-200"
                                        aria-expanded={isExpanded}
                                        aria-controls={detailsId}
                                    >
                                        {isExpanded ? 'Ocultar' : 'Ver mas'}
                                    </button>
                                </div>
                            </div>
                            <div className="mt-3 space-y-1 text-sm text-gray-700">
                                <p>Total: ${formatMoney(purchase.totalAmount)}</p>
                                {isCancelled && (
                                    <p className="text-xs text-red-700">
                                        Motivo anulacion: {cancelReasonText || 'Sin motivo registrado'}
                                    </p>
                                )}
                                <p>Fecha: {formatDateTime(purchase.createdAt)}</p>
                            </div>
                            {isExpanded && (
                                <div id={detailsId} className="mt-2 space-y-1 text-xs text-gray-600">
                                    <p>RUT: {clientRut}</p>
                                    <p>Condicion: {term === 'credit' ? 'Credito' : 'Contado'}</p>
                                    {purchase.balance !== undefined && (
                                        <p>Pendiente: ${formatMoney(purchase.balance || 0)}</p>
                                    )}
                                    {term === 'credit' && dueDate && (
                                        <p className={overdue ? 'text-red-600' : 'text-gray-600'}>
                                            Vence: {formatDate(dueDate)}{overdue ? ' (Vencido)' : ''}
                                        </p>
                                    )}
                                    {isCancelled && (
                                        <p className="text-red-700">
                                            Motivo anulacion: {cancelReasonText || 'Sin motivo registrado'}
                                        </p>
                                    )}
                                </div>
                            )}
                            <div className="mt-4 flex flex-wrap gap-2">
                                <button
                                    onClick={() => copyToClipboard(buildPurchaseCopy(purchase))}
                                    className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 flex items-center gap-2"
                                    type="button"
                                >
                                    <FiCopy size={16} />
                                    Copiar
                                </button>
                                {purchase.isDeleted ? (
                                    <button
                                        onClick={() => navigate(`/branches/${branchId}/purchases/${purchase.id}`)}
                                        className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 flex items-center gap-2"
                                    >
                                        <FiEye />
                                        Ver
                                    </button>
                                ) : isClosed ? (
                                    <>
                                        {canReadSales && (
                                            <button
                                                onClick={() => navigate(`/branches/${branchId}/purchases/${purchase.id}`)}
                                                className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 flex items-center gap-2"
                                            >
                                                <FiEye />
                                                Ver
                                            </button>
                                        )}
                                        {canUpdateSale && (
                                            <button
                                                onClick={() => navigate(`/branches/${branchId}/purchases/${purchase.id}/receipt`)}
                                                className="bg-emerald-500 text-white px-4 py-2 rounded hover:bg-emerald-600"
                                            >
                                                Comprobante
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {canDelete && (
                                            <button
                                                onClick={() => handleDelete(purchase)}
                                                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                                            >
                                                <FiTrash2 size={16} />
                                            </button>
                                        )}
                                        {canReadSales && (
                                            <button
                                                onClick={() => navigate(`/branches/${branchId}/purchases/${purchase.id}`)}
                                                className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 flex items-center gap-2"
                                            >
                                                <FiEye />
                                                Ver
                                            </button>
                                        )}
                                        {(() => {
                                            const statusNormalized = (purchase.saleStatus || '').toLowerCase();
                                            const isOpen = statusNormalized === 'open';
                                            const isSuspended = statusNormalized === 'suspended';
                                            const needsPayment =
                                                statusNormalized === 'completed'
                                                && !purchase.isDeleted
                                                && purchase.paymentTerm !== 'credit'
                                                && !purchase.paymentMethod
                                                && !purchase.isPaid;
                                            if (canUpdateSale && (isOpen || isSuspended)) {
                                                return (
                                                    <button
                                                        onClick={() => navigate(`/branches/${branchId}/purchases/${purchase.id}/edit`)}
                                                        className="bg-amber-500 text-white px-4 py-2 rounded hover:bg-amber-600"
                                                    >
                                                        Continuar compra
                                                    </button>
                                                );
                                            }
                                            if (needsPayment) {
                                                return (
                                                    <button
                                                        onClick={() => navigate(`/branches/${branchId}/purchases/${purchase.id}/receipt`)}
                                                        className="bg-emerald-500 text-white px-4 py-2 rounded hover:bg-emerald-600"
                                                    >
                                                        Registrar pago
                                                    </button>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        );
    };

    const renderQuotations = () => {
        if (!hasBranchContext) {
            return <p className="text-sm text-gray-600">Selecciona una sucursal para ver cotizaciones.</p>;
        }
        if (loadingQuotations) {
            return (
                <p className="text-sm text-gray-600">
                    <InlineLoading label="Cargando cotizaciones" />
                </p>
            );
        }
        if (quotationsError) {
            return <p className="text-sm text-red-600">{quotationsError}</p>;
        }
        return (
            <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <li className="border border-dashed border-blue-200 rounded-lg p-4 bg-blue-50/30 flex items-center justify-between">
                    <div>
                        <p className="text-sm font-semibold">Crear nueva cotizacion</p>
                        <p className="text-xs text-gray-600">Usa los datos del cliente.</p>
                    </div>
                    {canCreateQuotation && (
                        <button
                            onClick={() => navigate(`/branches/${branchId}/quotations/new?clientId=${id}`)}
                            className="bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600"
                        >
                            Nueva
                        </button>
                    )}
                </li>
                {filteredQuotations.map((q) => (
                    <li key={q.id} className="border border-gray-200 rounded-lg p-4 shadow-sm bg-white">
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <p className="text-xs text-gray-500">Cotizacion #{q.id}</p>
                                <p className="text-sm text-gray-700">
                                    Estado: {getQuotationStatusLabel(q.status)}
                                </p>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                                <span className="text-xs text-gray-500">{formatDateTime(q.createdAt)}</span>
                                <button
                                    type="button"
                                    onClick={() => toggleQuotationDetails(q.id)}
                                    className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded hover:bg-gray-200"
                                    aria-expanded={expandedQuotations.has(q.id)}
                                    aria-controls={`client-quotation-${q.id}-details`}
                                >
                                    {expandedQuotations.has(q.id) ? 'Ocultar' : 'Ver mas'}
                                </button>
                            </div>
                        </div>
                        {!clientEmail && (
                            <p className="text-xs text-red-600 mt-2">
                                Sin email: no se puede enviar por correo.
                            </p>
                        )}
                        <p className="text-sm mt-2">Total: ${formatMoney(q.totalAmount || 0)}</p>
                        {expandedQuotations.has(q.id) && (
                            <div id={`client-quotation-${q.id}-details`} className="mt-2 space-y-1 text-xs text-gray-600">
                                {q.updatedAt && (
                                    <p>Actualizada: {formatDateTime(q.updatedAt)}</p>
                                )}
                                <p>Email cliente: {clientEmail || 'Sin email'}</p>
                            </div>
                        )}
                        <div className="flex flex-wrap gap-2 mt-3">
                            <button
                                onClick={() => copyToClipboard(buildQuotationCopy(q))}
                                className="flex items-center gap-1 bg-gray-200 text-gray-800 px-3 py-2 rounded hover:bg-gray-300 text-sm"
                                type="button"
                            >
                                <FiCopy size={14} /> Copiar
                            </button>
                            {canReadQuotation && (
                                <button
                                    onClick={() => navigate(`/branches/${branchId}/quotations/${q.id}`)}
                                    className="bg-gray-200 text-gray-800 px-3 py-2 rounded hover:bg-gray-300"
                                >
                                    Ver
                                </button>
                            )}
                            {canUpdateQuotation && (
                                <button
                                    onClick={() => navigate(`/branches/${branchId}/quotations/${q.id}/edit`)}
                                    className="bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600"
                                >
                                    Editar
                                </button>
                            )}
                            {canCreateQuotation && (
                                <button
                                    onClick={() => handleDuplicateQuotation(q.id)}
                                    className="flex items-center gap-1 bg-amber-500 text-white px-3 py-2 rounded hover:bg-amber-600 text-sm"
                                >
                                    <FiCopy size={14} /> Duplicar
                                </button>
                            )}
                        </div>
                    </li>
                ))}
                {filteredQuotations.length === 0 && (
                    <p className="text-center col-span-full text-gray-600">Sin cotizaciones en el rango seleccionado.</p>
                )}
            </ul>
        );
    };

    const renderPayments = () => {
        const hasPurchasePayments = filteredPurchasePayments.length > 0;
        const hasClientPayments = filteredClientPayments.length > 0;
        const hasAnyPayments = hasPurchasePayments || hasClientPayments;

        return (
            <div className="space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 flex-1">
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                            <p className="text-xs uppercase text-amber-700">Saldo pendiente</p>
                            <p className="text-xl font-semibold text-amber-900">
                                ${formatMoney(effectiveBalance)}
                            </p>
                        </div>
                        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                            <p className="text-xs uppercase text-red-700">Vencido</p>
                            <p className="text-xl font-semibold text-red-900">
                                ${formatMoney(effectiveOverdue)}
                            </p>
                        </div>
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                            <p className="text-xs uppercase text-emerald-700">Credito a favor</p>
                            <p className="text-xl font-semibold text-emerald-900">
                                ${formatMoney(summaryCredit)}
                            </p>
                        </div>
                    </div>
                    {canUpdateClient && (
                        <button
                            onClick={() => setShowPaymentModal(true)}
                            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                            type="button"
                        >
                            Registrar pago
                        </button>
                    )}
                </div>

                {!hasBranchContext && (
                    <p className="text-sm text-gray-600">
                        Selecciona una sucursal para ver pagos por venta y saldos por compras.
                    </p>
                )}

                {hasBranchContext && openPurchases.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Compras pendientes</h3>
                        <div className="space-y-2">
                            {openPurchases.slice(0, 5).map((purchase) => (
                                <button
                                    key={purchase.id}
                                    type="button"
                                    onClick={() => navigate(`/branches/${branchId}/purchases/${purchase.id}`)}
                                    className="w-full text-left border border-gray-200 rounded-lg p-3 bg-white hover:border-blue-200 hover:bg-blue-50/40"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-800">
                                            {docLabel(purchase)} #{purchase.id}
                                        </span>
                                        <span className="text-xs text-amber-700">
                                            Pendiente: ${formatMoney(purchase.balance || 0)}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {formatDateTime(purchase.createdAt)}
                                    </p>
                                </button>
                            ))}
                            {openPurchases.length > 5 && (
                                <p className="text-xs text-gray-500">
                                    +{openPurchases.length - 5} compras pendientes adicionales.
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {clientPaymentsError && (
                    <p className="text-sm text-red-600">{clientPaymentsError}</p>
                )}
                {purchasesError && hasBranchContext && (
                    <p className="text-sm text-red-600">{purchasesError}</p>
                )}

                {(loadingClientPayments || (hasBranchContext && loadingPurchases)) && (
                    <p className="text-sm text-gray-600">
                        <InlineLoading label="Cargando pagos" />
                    </p>
                )}

                {!loadingClientPayments && !loadingPurchases && !hasAnyPayments && (
                    <p className="text-sm text-gray-600">Sin pagos en el rango seleccionado.</p>
                )}

                {hasClientPayments && (
                    <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Pagos generales</h3>
                        <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {filteredClientPayments.map(({ payment }) => (
                                <li key={payment.id} className="border border-gray-200 rounded-lg p-4 shadow-sm bg-white">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <p className="text-xs text-gray-500">Pago cliente #{payment.id}</p>
                                            <p className="text-lg font-semibold text-gray-900">
                                                ${formatMoney(payment.amount)}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                Metodo: {payment.method}
                                            </p>
                                        </div>
                                        <span className="text-xs text-gray-500">
                                            {formatDateTime(payment.paidAt || payment.createdAt)}
                                        </span>
                                    </div>
                                    <div className="mt-3 space-y-1 text-sm text-gray-700">
                                        {payment.reference && <p>Referencia: {payment.reference}</p>}
                                        {payment.note && <p>Nota: {payment.note}</p>}
                                    </div>
                                    {payment.voucherUrl && (
                                        <div className="mt-4">
                                            <a
                                                href={payment.voucherUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600 inline-block"
                                            >
                                                Comprobante
                                            </a>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {hasPurchasePayments && (
                    <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Pagos por venta</h3>
                        <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {filteredPurchasePayments.map(({ payment, purchase }) => (
                                <li key={payment.id} className="border border-gray-200 rounded-lg p-4 shadow-sm bg-white">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <p className="text-xs text-gray-500">Pago #{payment.id}</p>
                                            <p className="text-lg font-semibold text-gray-900">
                                                ${formatMoney(payment.amount)}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                Metodo: {payment.method}
                                            </p>
                                        </div>
                                        <span className="text-xs text-gray-500">
                                            {formatDateTime(payment.paidAt || payment.createdAt)}
                                        </span>
                                    </div>
                                    <div className="mt-3 space-y-1 text-sm text-gray-700">
                                        <p>
                                            Venta: {docLabel(purchase)} #{purchase.id}
                                        </p>
                                        {payment.reference && <p>Referencia: {payment.reference}</p>}
                                        {payment.note && <p>Nota: {payment.note}</p>}
                                    </div>
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        <button
                                            onClick={() => navigate(`/branches/${branchId}/purchases/${purchase.id}`)}
                                            className="bg-gray-200 text-gray-800 px-3 py-2 rounded hover:bg-gray-300"
                                            type="button"
                                        >
                                            Ver venta
                                        </button>
                                        {payment.voucherUrl && (
                                            <a
                                                href={payment.voucherUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600"
                                            >
                                                Comprobante
                                            </a>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    const renderGuides = () => {
        if (!hasBranchContext) {
            return <p className="text-sm text-gray-600">Selecciona una sucursal para ver guias de despacho.</p>;
        }
        if (loadingGuides) {
            return (
                <p className="text-sm text-gray-600">
                    <InlineLoading label="Cargando guias de despacho" />
                </p>
            );
        }
        if (guidesError) {
            return <p className="text-sm text-red-600">{guidesError}</p>;
        }
        if (filteredGuides.length === 0) {
            return <p className="text-sm text-gray-600">Sin guias en el rango seleccionado.</p>;
        }
        return (
            <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredGuides.map((guide) => {
                    const guideNumber = guide.guideNumber || guide.id;
                    const statusLabel = getDeliveryGuideStatusLabel(guide.status);
                    return (
                        <li key={guide.id} className="border border-gray-200 rounded-lg p-4 shadow-sm bg-white">
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <p className="text-xs text-gray-500">Guia #{guideNumber}</p>
                                    <p className="text-sm text-gray-700">Estado: {statusLabel}</p>
                                    <p className="text-sm text-gray-700">
                                        Venta: #{guide.purchaseId}
                                    </p>
                                </div>
                                <span className="text-xs text-gray-500">
                                    {formatDate(guide.deliveryDate || guide.createdAt)}
                                </span>
                            </div>
                            {guide.notes && (
                                <p className="text-xs text-gray-600 mt-2">Nota: {guide.notes}</p>
                            )}
                            <div className="mt-4">
                                <button
                                    onClick={() => navigate(`/branches/${branchId}/delivery-guides/${guide.id}/details`)}
                                    className="bg-gray-200 text-gray-800 px-3 py-2 rounded hover:bg-gray-300"
                                >
                                    Ver guia
                                </button>
                            </div>
                        </li>
                    );
                })}
            </ul>
        );
    };

    const handleDuplicateQuotation = async (quotationId) => {
        if (!canCreateQuotation) return;
        try {
            const resp = await duplicateQuotation(branchId, quotationId);
            if (resp?.quotation?.id) {
                navigate(`/branches/${branchId}/quotations/${resp.quotation.id}`);
            }
        } catch (err) {
            console.error(err);
            notify('No se pudo duplicar la cotización.');
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 pt-24 px-4 pb-10">
            <div className="max-w-6xl mx-auto space-y-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold">{clientName}</h1>
                            <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-gray-600">
                                <span className="px-2 py-1 rounded bg-gray-100 text-gray-700">
                                    Rut: {clientRut}
                                </span>
                                {client?.giro && (
                                    <span className="px-2 py-1 rounded bg-amber-100 text-amber-700">
                                        Giro: {client.giro}
                                    </span>
                                )}
                                {canManageClientPricing && (
                                    <span className={`inline-flex items-center gap-1 rounded px-2 py-1 ${
                                        clientPricingCategoryName
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'bg-gray-100 text-gray-600'
                                    }`}
                                    >
                                        <FiTag />
                                        {clientPricingCategoryName || 'Sin descuento'}
                                    </span>
                                )}
                            </div>
                            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                                <span>Email: {clientEmail || '—'}</span>
                                <span>Telefono: {client?.phone || '—'}</span>
                                <span>Direccion: {client?.address || '—'}</span>
                                <span>Ciudad: {client?.city || '—'}</span>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {canUpdateClient && (
                                <button
                                    onClick={() => setShowPaymentModal(true)}
                                    className="bg-emerald-500 text-white px-4 py-2 rounded hover:bg-emerald-600"
                                >
                                    Registrar pago
                                </button>
                            )}
                            {canUpdateClient && (
                                <button
                                    onClick={() => navigate(`/client/${id}/edit`)}
                                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                                >
                                    Editar cliente
                                </button>
                            )}
                            <button
                                onClick={() => navigate('/client')}
                                className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                            >
                                Volver
                            </button>
                        </div>
                    </div>

                    {(loadingClient || loadingSummary) && (
                        <p className="text-sm text-gray-500 mt-3">
                            <InlineLoading label="Cargando informacion del cliente" />
                        </p>
                    )}
                    {clientError && (
                        <p className="text-sm text-red-600 mt-3">{clientError}</p>
                    )}
                    {summaryError && (
                        <p className="text-sm text-red-600 mt-3">{summaryError}</p>
                    )}

                    {!clientEmail && (
                        <div className="mt-4">
                            <span className="text-xs text-red-600">
                                Sin email: no se puede enviar cotizaciones.
                            </span>
                        </div>
                    )}

                    {!hasBranchContext && (
                        <div className="mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                            Selecciona una sucursal para ver compras, cotizaciones, pagos y guias.
                        </div>
                    )}

                    {client?.account_available && (
                        <div className="mt-4 space-y-3">
                            <ClientQuotationCategoriesEditor
                                clientId={id}
                                enabled={client.account_available}
                                canEdit={canUpdateClient}
                            />
                            <ClientQuotationCategoriesEditor
                                clientId={id}
                                enabled={client.account_available}
                                canEdit={canUpdateClient}
                                scope="purchaseOrder"
                            />
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex flex-wrap gap-2">
                        {tabConfig.map((tab) => {
                            const isActive = activeTab === tab.id;
                            const isDisabled = tab.requiresBranch && !hasBranchContext;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setActiveTab(tab.id)}
                                    disabled={isDisabled}
                                    className={`px-4 py-2 rounded text-sm border ${
                                        isActive
                                            ? 'bg-blue-500 text-white border-blue-500'
                                            : 'bg-white text-gray-600 border-gray-200 hover:border-blue-200'
                                    } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {shouldShowFilters && (hasBranchContext || activeTab === 'payments') && (
                    <PurchaseFilters
                        filters={filters}
                        setters={setters}
                        containerClassName="bg-white"
                        showDocumentType={activeTab === 'purchases'}
                        documentType={documentTypeFilter}
                        onDocumentTypeChange={setDocumentTypeFilter}
                        showPaymentStatus={activeTab === 'purchases'}
                        paymentFilter={paymentFilter}
                        onPaymentFilterChange={setPaymentFilter}
                        showDateRange={activeTab !== 'purchases'}
                        showDueRange={activeTab !== 'purchases'}
                        useSelectFilters={activeTab === 'purchases'}
                        layout={activeTab === 'purchases' ? 'row' : 'default'}
                    />
                )}

                <div className="bg-white rounded-lg shadow p-6">
                    {activeTab === 'summary' && renderSummary()}
                    {activeTab === 'purchases' && renderPurchases()}
                    {activeTab === 'quotations' && renderQuotations()}
                    {activeTab === 'payments' && renderPayments()}
                    {activeTab === 'guides' && renderGuides()}
                </div>
                <CancelPendingSaleModal
                    isOpen={!!cancelTarget}
                    onClose={() => setCancelTarget(null)}
                    saleLabel={cancelTarget ? `${docLabel(cancelTarget)} #${cancelTarget.id}` : ''}
                    onConfirm={handleConfirmCancel}
                />
                {showPaymentModal && (
                    <ClientPaymentModal
                        client={client}
                        summary={clientSummary}
                        onClose={() => setShowPaymentModal(false)}
                        onSaved={handlePaymentSaved}
                        canUpdateClient={canUpdateClient}
                    />
                )}
                {showCreditModal && client && (
                    <ClientCreditModal
                        client={client}
                        canManageCredit={canManageCredit}
                        onClose={() => setShowCreditModal(false)}
                        onSaved={handleCreditSaved}
                        canUpdateClient={canUpdateClient}
                    />
                )}
            </div>
        </div>
    );
};

export default ClientPurchasesPage;
